import { Fragment, useEffect, useState } from "react";
import { CircleMarker, Marker, Polygon, Polyline, useMap, useMapEvents } from "react-leaflet";
import { getGeometry } from "../services/apiService";
import { convertFeatures } from "../utils/coordsToLatLngs";
import { useAuth } from "../context/AuthContext";
import ReactDOMServer from "react-dom/server";
import L from "leaflet";

interface GeometryLoaderProps {
  onLayerClick?: (layer: any) => void;
  renderer: L.Renderer;
  pointPane: string;
}

const GeometryLoader = ({ onLayerClick, renderer, pointPane }: GeometryLoaderProps) => {
  const map = useMap();
  const { getToken } = useAuth();

  const [data, setData] = useState<any[]>([]);
  useEffect(() => {
    loadAssetData();
  }, []);

  useMapEvents({
    moveend() {
      loadAssetData();
    },
  });

  const loadAssetData = async () => {
    const token = await getToken();
    getGeometry(token, map.getBounds())
      .then((result) => {
        const features = convertFeatures(result.data.features);
        setData(features);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const styleHeaderText = (properties: any) => {
    const badge = L.divIcon({
      className: "",
      html: ReactDOMServer.renderToString(
        <div
          className="badge p-0"
          style={{
            fontWeight: 700,
            lineHeight: 1,
            whiteSpace: "nowrap",
            color: properties.bgColor,
            fontSize: `${map.getZoom()}px`,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {properties.headertext}
        </div>,
      ),
    });
    return badge;
  };

  const headerPosition = (coords: any) => {
    return L.polygon(coords).getBounds().getCenter();
  };

  const handleLayerClick = (layer: any) => {
    if (layer && layer.details) {
      onLayerClick?.(layer.details);
    }
  };

  return (
    <>
      {data &&
        data.length > 0 &&
        data.map((layer) => (
          <Fragment key={layer.id}>
            {["Polygon", "MultiPolygon"].includes(layer.geometry.type) && (
              <>
                <Marker
                  position={headerPosition(layer.geometry.coordinates)}
                  icon={styleHeaderText(layer.properties)}
                  pane={pointPane}
                  eventHandlers={{
                    click: () => {
                      handleLayerClick(layer);
                    },
                  }}
                />
                <Polygon
                  positions={layer.geometry.coordinates}
                  pathOptions={{
                    fillColor: layer.properties.bgColor,
                    color: layer.properties.color,
                    opacity: layer.properties.bgColorOpacity,
                  }}
                  renderer={renderer}
                  eventHandlers={{
                    click: () => {
                      handleLayerClick(layer);
                    },
                  }}
                />
              </>
            )}
            {["LineString", "MultiLineString"].includes(layer.geometry.type) && (
              <>
                <Marker
                  position={headerPosition(layer.geometry.coordinates)}
                  icon={styleHeaderText(layer.properties)}
                  pane={pointPane}
                  eventHandlers={{
                    click: () => {
                      handleLayerClick(layer);
                    },
                  }}
                />
                <Polyline
                  positions={layer.geometry.coordinates}
                  pathOptions={{
                    fillColor: layer.properties.bgColor,
                    color: layer.properties.color,
                    opacity: layer.properties.bgColorOpacity,
                    weight: layer.properties.size,
                  }}
                  renderer={renderer}
                  eventHandlers={{
                    click: () => {
                      handleLayerClick(layer);
                    },
                  }}
                />
              </>
            )}
            {["Marker", "Point"].includes(layer.geometry.type) && (
              <CircleMarker
                center={layer.geometry.coordinates}
                pathOptions={{
                  fillColor: layer.properties.bgColor,
                  color: layer.properties.color,
                  opacity: layer.properties.bgColorOpacity,
                }}
                radius={layer.properties.size}
                pane={pointPane}
                eventHandlers={{
                  click: () => {
                    handleLayerClick(layer);
                  },
                }}
              />
            )}
          </Fragment>
        ))}
    </>
  );
};

export default GeometryLoader;
